import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    forwardRef,
    inject,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatepickerFieldComponent } from '../datepicker-field/datepicker-field.component';
import { ErrorFieldComponent } from '../error-field/error-field.component';
import { SelectFieldComponent } from '../select-field/select-field.component';
import { StrutturaSelector } from '../struttura-selector/struttura-selector.component';

import { CommonModule } from '@angular/common';
import { MatFormFieldAppearance } from '@angular/material/form-field';

/**
 * A ruolo/responsabile option as shown in the selects.
 */
export interface RuoloOption {
  id: number;
  name: string;
}

/**
 * A struttura reference, as emitted by `strutturaChanged`.
 */
export interface StrutturaRuoloRef extends RuoloOption {
  isAdministrator: boolean;
}

/**
 * The data used to prefill a single ruolo/date row's FormGroup.
 */
export interface RuoloDateGroupData {
  ruolo?: RuoloOption | null;
  dataInizio?: string | Date | null;
  dataFine?: string | Date | null;
}

/**
 * A single entry of the control's external (model) value.
 */
export interface RuoloDateEntry {
  ruoloSanitario?: {
    struttura?: StrutturaRuoloRef | null;
    ruolo?: RuoloOption | null;
  } | null;
  dataInizio?: string | Date | null;
  dataFine?: string | Date | null;
  responsabile?: RuoloOption | null;
}

@UntilDestroy()
@Component({
   
  selector: 'siat-struttura-ruolo-dates-selector',
  templateUrl: './struttura-ruolo-dates-selector.component.html',
  styleUrls: ['./struttura-ruolo-dates-selector.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StrutturaSelector,
    DatepickerFieldComponent,
    SelectFieldComponent,
    ErrorFieldComponent,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TranslatePipe
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => StrutturaRuoloDatesSelectorComponent),
      multi: true,
    },
  ],
})
export class StrutturaRuoloDatesSelectorComponent
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input() appearance: MatFormFieldAppearance = 'outline';
  @Input() id?: string;
  @Input() name?: string;
  @Input() label = '';
  @Input() placeholder?: string;
  @Input() readonly = false;
  @Input() disableStruttura = false;
  @Input() ruoliOptions: RuoloOption[] = [];
  @Input() responsabileOptions: RuoloOption[] = [];
  @Input() idStrutturaDefault?: number;
  @Output() optionReady = new EventEmitter<boolean>();
  @Output() strutturaChanged = new EventEmitter<StrutturaRuoloRef>();

  private fb = inject(FormBuilder);
  private translateService = inject(TranslateService);

  formGroup!: FormGroup;

  get ruoloDateArray(): FormArray {
    return this.formGroup.get('ruoloDateEntries') as FormArray;
  }

  get ruoloDateGroups(): FormGroup[] {
    return this.ruoloDateArray.controls as FormGroup[];
  }

  get selectedStruttura() {
    return this.formGroup.get('struttura')?.value;
  }

  // ---- CVA ----
  private onChange: (value: RuoloDateEntry[]) => void = () => {};
  private onTouched: () => void = () => {};

  registerOnChange(fn: (value: RuoloDateEntry[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(isDisabled: boolean): void {
    this.readonly = isDisabled;
    if (isDisabled) {
      this.formGroup.disable();
    } else {
      this.formGroup.enable();
      // Re-apply disableStruttura if it was set
      if (this.disableStruttura) {
        this.formGroup.get('struttura')?.disable();
      }
    }
  }
  writeValue(value: RuoloDateEntry[] | null): void {
    // Every mutation below passes emitEvent: false: this is a programmatic
    // restore of previously-saved data, not a user edit, so it must not
    // notify the parent form or fire strutturaChanged (which would trigger
    // an unwanted ruoli reload for a struttura we're not actually changing).
    if (value && Array.isArray(value) && value.length > 0) {
      // Extract struttura and responsabile from first entry (all should have same struttura)
      const firstEntry = value[0];
      this.formGroup.patchValue(
        {
          struttura: firstEntry?.ruoloSanitario?.struttura || null,
          responsabile: firstEntry?.responsabile || null,
        },
        { emitEvent: false }
      );

      // Clear existing entries
      this.ruoloDateArray.clear();

      // Add entries
      value.forEach(v => {
        const ruoloDateEntry = {
          ruolo: v?.ruoloSanitario?.ruolo || null,
          dataInizio: v?.dataInizio || null,
          dataFine: v?.dataFine || null,
        };
        const group = this.createRuoloDateGroup(ruoloDateEntry);
        this.ruoloDateArray.push(group, { emitEvent: false });
        this.setupDateValidation(group);
      });

      // Re-apply disableStruttura after setting values
      if (this.disableStruttura) {
        this.formGroup.get('struttura')?.disable({ emitEvent: false });
      }
    } else {
      // Reset form
      this.formGroup.patchValue(
        { struttura: null, responsabile: null },
        { emitEvent: false }
      );
      this.ruoloDateArray.clear();
      const group = this.createRuoloDateGroup();
      this.ruoloDateArray.push(group, { emitEvent: false });
      this.setupDateValidation(group);

      // Re-apply disableStruttura after reset
      if (this.disableStruttura) {
        this.formGroup.get('struttura')?.disable({ emitEvent: false });
      }
    }
  }

  constructor() {
    const initialGroup = this.createRuoloDateGroup();
    this.formGroup = this.fb.group({
      struttura:  [null, [Validators.required]],
      responsabile: [{ value: null, disabled: true }],
      ruoloDateEntries: this.fb.array([initialGroup]),
    });
    this.setupDateValidation(initialGroup);
  }

  ngOnInit(): void {
    // Apply initial disableStruttura state (the @Input is already set by
    // Angular before ngOnInit runs, so a single synchronous check suffices).
    if (this.disableStruttura) {
      this.formGroup.get('struttura')?.disable();
    }

    // Listen for struttura changes. writeValue() patches with emitEvent:
    // false, so this only fires for genuine user selections.
    this.formGroup.get('struttura')?.valueChanges.subscribe(struttura => {
      if (struttura) {
        this.strutturaChanged.emit(struttura);
        // Clear all ruolo selections when struttura changes
        this.ruoloDateGroups.forEach(group => {
          group.get('ruolo')?.setValue(null);
        });
      }
    });

    // ValueChanges -> CVA
    this.formGroup.valueChanges.subscribe(() => {
      // Validate duplicate roles with overlapping dates
      this.validateDuplicateRoles();
      
      const formValue = this.formGroup.value;
      const struttura = formValue.struttura;
      
      if (struttura && formValue.ruoloDateEntries) {
        // Transform back to the expected format including responsabile
        const transformedValue = formValue.ruoloDateEntries.map(
          (entry: RuoloDateGroupData) => ({
            ruoloSanitario: {
              struttura: struttura,
              ruolo: entry.ruolo,
            },
            dataInizio: entry.dataInizio,
            dataFine: entry.dataFine,
            responsabile: formValue.responsabile,
          })
        );
        this.onChange(transformedValue);
      } else {
        this.onChange([]);
      }
      this.onTouched();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['readonly'] && this.readonly) {
      // When readonly becomes true, disable the entire form
      this.formGroup.disable();
    }
    
    // Handle disableStruttura changes
    if (changes['disableStruttura']) {
      const strutturaControl = this.formGroup.get('struttura');
      if (this.disableStruttura) {
        strutturaControl?.disable();
      } else if (!this.readonly) {
        strutturaControl?.enable();
      }
    }
    
    // Handle ruoliOptions changes - update form values if options are loaded after initial value set
    if (changes['ruoliOptions'] && changes['ruoliOptions'].currentValue) {
      this.updateFormWithNewRuoliOptions();
    }
  }

  private createRuoloDateGroup(data?: RuoloDateGroupData): FormGroup {
    if (this.readonly) {
      // In readonly mode, create disabled controls
      return this.fb.group({
        ruolo: [{ value: data?.ruolo || null, disabled: true }],
        dataInizio: [{ value: data?.dataInizio || null, disabled: true }],
        dataFine: [{ value: data?.dataFine || null, disabled: true }],
      });
    } else {
      const group = this.fb.group({
        ruolo: [data?.ruolo || null, [Validators.required]],
        dataInizio: [data?.dataInizio || null, [Validators.required]],
        dataFine: [data?.dataFine || null],
      });

      // Add custom validator to the group for date range validation
      group.setValidators(this.dateRangeValidator);
      return group;
    }
  }

  addRuoloDateEntry(): void {
    const newGroup = this.createRuoloDateGroup();
    this.ruoloDateArray.push(newGroup);
    this.setupDateValidation(newGroup);
  }

  removeRuoloDateEntry(index: number): void {
    if (this.ruoloDateArray.length > 1) {
      this.ruoloDateArray.removeAt(index);
    }
  }

  clearStruttura(): void {
    this.formGroup.get('struttura')?.reset();
  }

  clearResponsabile(): void {
    this.formGroup.get('responsabile')?.reset();
  }

  clearRuolo(index: number): void {
    this.ruoloDateGroups[index]?.get('ruolo')?.reset();
  }

  private setupDateValidation(group: FormGroup): void {
    if (this.readonly) {
      return;
    }

    // Subscribe to date changes to trigger validation
    group.get('dataInizio')?.valueChanges.subscribe(() => {
      group.get('dataFine')?.updateValueAndValidity({ emitEvent: false });
    });

    group.get('dataFine')?.valueChanges.subscribe(() => {
      group.get('dataInizio')?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private dateRangeValidator = (group: AbstractControl): ValidationErrors | null => {
    const dataInizio = group.get('dataInizio')?.value;
    const dataFine = group.get('dataFine')?.value;

    if (!dataInizio || !dataFine) {
      return null; // Don't validate if either date is missing
    }

    const startDate = new Date(dataInizio);
    const endDate = new Date(dataFine);

    if (startDate >= endDate) {
      const errorMessage = this.translateService.instant('FORM_VALIDATION.DATE_RANGE_ERROR');
      return { dateRange: { message: errorMessage } };
    }

    return null;
  }


  getDateRangeError(groupIndex: number): string | null {
    const group = this.ruoloDateGroups[groupIndex];
    if (!group) return null;
    
    const errors = group.errors;
    if (errors?.['dateRange']) {
      return errors['dateRange'].message;
    }
    return null;
  }

  shouldShowDateRangeError(groupIndex: number): boolean {
    const group = this.ruoloDateGroups[groupIndex];
    if (!group) return false;
    
    const dataInizioTouched = group.get('dataInizio')?.touched;
    const dataFineTouched = group.get('dataFine')?.touched;
    return (dataInizioTouched || dataFineTouched) && !!this.getDateRangeError(groupIndex);
  }
  
  private updateFormWithNewRuoliOptions(): void {
    // Update existing form groups with new ruoli options if they have ruolo values but weren't found before
    this.ruoloDateGroups.forEach(group => {
      const currentRuoloValue = group.get('ruolo')?.value;
      if (currentRuoloValue && currentRuoloValue.id && this.ruoliOptions.length > 0) {
        // Find the matching option in the new ruoli options
        const matchingOption = this.ruoliOptions.find(option => option.id === currentRuoloValue.id);
        if (matchingOption) {
          // Update the form control with the complete option data
          group.get('ruolo')?.setValue(matchingOption, { emitEvent: false });
        }
      }
    });
  }

  private validateDuplicateRoles(): void {
    const groups = this.ruoloDateGroups;
    
    // Clear existing duplicate role errors
    groups.forEach(group => {
      const currentErrors = group.errors || {};
      delete currentErrors['duplicateRole'];
      if (Object.keys(currentErrors).length === 0) {
        group.setErrors(null);
      } else {
        group.setErrors(currentErrors);
      }
    });

    // Check for duplicate roles with overlapping dates
    for (let i = 0; i < groups.length; i++) {
      const currentGroup = groups[i];
      const currentRuolo = currentGroup.get('ruolo')?.value;
      const currentDataInizio = currentGroup.get('dataInizio')?.value;
      const currentDataFine = currentGroup.get('dataFine')?.value;

      if (!currentRuolo || !currentDataInizio) continue;

      // Convert to dates for comparison
      const currentStartDate = new Date(currentDataInizio);
      const currentEndDate = currentDataFine ? new Date(currentDataFine) : new Date('9999-12-31');

      // Check against all previous entries for duplicates
      for (let j = 0; j < i; j++) {
        const otherGroup = groups[j];
        const otherRuolo = otherGroup.get('ruolo')?.value;
        const otherDataInizio = otherGroup.get('dataInizio')?.value;
        const otherDataFine = otherGroup.get('dataFine')?.value;

        if (!otherRuolo || !otherDataInizio) continue;

        // Check if same role
        if (currentRuolo.id === otherRuolo.id) {
          const otherStartDate = new Date(otherDataInizio);
          const otherEndDate = otherDataFine ? new Date(otherDataFine) : new Date('9999-12-31');

          // Check for date overlap
          const hasOverlap = currentStartDate <= otherEndDate && currentEndDate >= otherStartDate;

          if (hasOverlap) {
            const errorMessage = this.translateService.instant('FORM_VALIDATION.DUPLICATE_ROLE_DATE_OVERLAP');
            const currentErrors = currentGroup.errors || {};
            currentErrors['duplicateRole'] = { message: errorMessage };
            currentGroup.setErrors(currentErrors);
            break;
          }
        }
      }
    }
  }

  getDuplicateRoleError(groupIndex: number): string | null {
    const group = this.ruoloDateGroups[groupIndex];
    if (!group) return null;
    
    const errors = group.errors;
    if (errors?.['duplicateRole']) {
      return errors['duplicateRole'].message;
    }
    return null;
  }

  shouldShowDuplicateRoleError(groupIndex: number): boolean {
    const group = this.ruoloDateGroups[groupIndex];
    if (!group) return false;
    
    const ruoloTouched = group.get('ruolo')?.touched;
    const dataInizioTouched = group.get('dataInizio')?.touched;
    return (ruoloTouched || dataInizioTouched) && !!this.getDuplicateRoleError(groupIndex);
  }
}